import { useQuery } from '@tanstack/react-query';
import { BrainCircuit, FileText, Mic } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { http } from '@/api/http';
import { selectData } from '@/api/envelope';
import type { SuccessResponseDTO } from '@/api/envelope';
import { formatVerdictLabel, isDeepScanSettled, isProvisionalNoAction } from '@/features/moderation/lib/verdictDisplay';
import {
  isTipmiHidePending,
  isTipmiHoldPending,
  isTipmiPublishPending,
} from '@/features/moderation/lib/coopTipmiSync';
import { useContentIntelligence } from '../api';

interface ContentDescriptionCardProps {
  contentId: string;
  /** The post 'content' field from the detail DTO (caption/description). */
  description?: string | null | undefined;
  /** TipMi publish status — used when Coop reject has not yet HID, or approve has not yet PUB. */
  postStatus?: string | null | undefined;
  /** TipMi ledger — when PENDING, keep polling for a late Coop case. */
  moderationStatus?: string | null | undefined;
}

interface TranscriptionResponse {
  transcription: string | null;
  language: string | null;
}

/**
 * Shows the video/post description prominently, fetches video transcription,
 * and displays the Gemma AI content summary (from moderation intelligence verdict_reason).
 */
export function ContentDescriptionCard({
  contentId,
  description,
  postStatus,
  moderationStatus,
}: ContentDescriptionCardProps) {
  const id = Number(contentId);
  const valid = Number.isFinite(id) && id > 0;

  const transcriptionQuery = useQuery({
    queryKey: ['content', 'transcription', id],
    queryFn: async () => {
      const res = await http.get<SuccessResponseDTO<TranscriptionResponse>>(
        `/admin/api/post-manager/${id}/transcription`,
      );
      return selectData<TranscriptionResponse>(res.data);
    },
    enabled: valid,
    retry: false,
    staleTime: 300_000,
  });

  const intelligenceQuery = useContentIntelligence(contentId, {
    pollWhileMissing: (moderationStatus ?? '').trim().toUpperCase() === 'PENDING',
  });

  const hasDescription = description && description.trim().length > 0;
  const hasTranscription =
    transcriptionQuery.data?.transcription && transcriptionQuery.data.transcription.trim().length > 0;
  const hasAiSummary =
    intelligenceQuery.data?.verdict_reason && intelligenceQuery.data.verdict_reason.trim().length > 0;
  const hidePending = isTipmiHidePending(intelligenceQuery.data?.verdict, postStatus);
  const holdPending = isTipmiHoldPending(intelligenceQuery.data?.verdict, postStatus);
  const publishPending = isTipmiPublishPending(intelligenceQuery.data?.verdict, postStatus);
  const tipmiLedgerPending = (moderationStatus ?? '').trim().toUpperCase() === 'PENDING';
  const deepScanSettled = isDeepScanSettled({
    deepScanDurationMs: intelligenceQuery.data?.deep_scan_duration_ms,
    deliveryStatus: intelligenceQuery.data?.delivery_status,
    vectors: intelligenceQuery.data?.vectors,
  });
  const provisional = isProvisionalNoAction(
    intelligenceQuery.data?.verdict,
    intelligenceQuery.data?.case_status,
    { deepScanSettled },
  );
  const verdictOpts = { deepScanSettled, tipmiLedgerPending };
  const verdictChip = hidePending
    ? `${formatVerdictLabel(intelligenceQuery.data?.verdict, intelligenceQuery.data?.case_status, verdictOpts)} · TipMi hide pending`
    : holdPending
      ? `${formatVerdictLabel(intelligenceQuery.data?.verdict, intelligenceQuery.data?.case_status, verdictOpts)} · TipMi hold pending`
      : publishPending
        ? `${formatVerdictLabel(intelligenceQuery.data?.verdict, intelligenceQuery.data?.case_status, verdictOpts)} · TipMi publish pending`
        : formatVerdictLabel(
            intelligenceQuery.data?.verdict,
            intelligenceQuery.data?.case_status,
            verdictOpts,
          );

  const tipmiDecidePending =
    !hidePending &&
    !holdPending &&
    !publishPending &&
    !provisional &&
    deepScanSettled &&
    tipmiLedgerPending;

  const isLoading = transcriptionQuery.isLoading || intelligenceQuery.isLoading;

  // Always render the card — it shows at least the loading state or description
  if (
    !hasDescription &&
    !hasTranscription &&
    !hasAiSummary &&
    !hidePending &&
    !holdPending &&
    !publishPending &&
    !provisional &&
    !tipmiDecidePending &&
    !isLoading
  ) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Content Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hidePending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            TipMi rule preview says Rejected, but TipMi is still not hidden (Waiting / Published —
            hide pending).
          </p>
        )}
        {holdPending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            TipMi rule preview says Escalated, but TipMi is still Published (hold pending — should
            move to Waiting). This is not a live Coop action.
          </p>
        )}
        {publishPending && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            TipMi rule preview says Approved, but TipMi is still held (Waiting — not published yet).
          </p>
        )}

        {/* AI Content Summary (Gemma) */}
        {intelligenceQuery.isLoading ? (
          <div className="space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : hasAiSummary || provisional ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BrainCircuit className="size-3.5" />
              AI Content Summary
              {intelligenceQuery.data?.verdict && (
                <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold">
                  {verdictChip}
                </span>
              )}
            </div>
            {hasAiSummary ? (
              <div className="rounded border border-border/50 bg-muted/30 p-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {intelligenceQuery.data?.verdict_reason}
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Pipeline in progress — summary will appear when the AI scan finishes.
              </p>
            )}
          </div>
        ) : null}

        {/* Description / Caption */}
        {hasDescription && (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <FileText className="size-3.5" />
              Description
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{description}</p>
          </div>
        )}

        {/* Transcription */}
        {transcriptionQuery.isLoading ? (
          <div className="space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : hasTranscription ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Mic className="size-3.5" />
              Video Transcription
              {transcriptionQuery.data?.language && (
                <span className="rounded bg-muted px-1 py-0.5 text-[10px]">
                  {transcriptionQuery.data.language}
                </span>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto rounded bg-muted/50 p-3">
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                {transcriptionQuery.data?.transcription}
              </p>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
